import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RealTimeDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [performance, setPerformance] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState(7);
  const [affiliateId, setAffiliateId] = useState('default_affiliate');

  useEffect(() => {
    fetchDashboardData();
    fetchPerformanceData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchPerformanceData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [affiliateId, selectedPlatform, selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tracking/dashboard/${affiliateId}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Erro ao carregar dados do dashboard');
    }
  };

  const fetchPerformanceData = async () => {
    try {
      const params = new URLSearchParams({
        days: selectedPeriod.toString()
      });
      
      if (selectedPlatform !== 'all') {
        params.append('platform', selectedPlatform);
      }
      
      const response = await axios.get(`${API_URL}/api/tracking/performance/${affiliateId}?${params}`);
      setPerformance(response.data);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      toast.error('Erro ao carregar dados de performance');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('pt-BR').format(number);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard em Tempo Real</h1>
        <p className="text-gray-600 mt-2">
          Acompanhe suas métricas de afiliados em tempo real - Atualização automática a cada 30 segundos
        </p>
        <div className="flex items-center mt-2 space-x-2">
          <div className="flex items-center">
            <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="ml-2 text-sm text-green-600 font-medium">Ao vivo</span>
          </div>
          <span className="text-sm text-gray-500">
            Última atualização: {new Date().toLocaleTimeString('pt-BR')}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plataforma</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">Todas as Plataformas</option>
              <option value="clickbank">ClickBank</option>
              <option value="monetizze">Monetizze</option>
              <option value="hotmart">Hotmart</option>
              <option value="amazon">Amazon Associates</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value={1}>Hoje</option>
              <option value={7}>Últimos 7 dias</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={90}>Últimos 90 dias</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-md">
              <span className="text-blue-600 text-2xl">👆</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Cliques</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(performance?.clicks?.total || 0)}
              </p>
              <p className="text-xs text-green-600">
                {performance?.clicks?.converted || 0} convertidos
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-md">
              <span className="text-green-600 text-2xl">🎯</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Taxa de Conversão</p>
              <p className="text-2xl font-bold text-gray-900">
                {(performance?.conversions?.rate || 0).toFixed(2)}%
              </p>
              <p className="text-xs text-blue-600">
                {performance?.conversions?.total || 0} conversões
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-md">
              <span className="text-yellow-600 text-2xl">💰</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Receita Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(performance?.revenue?.total_sales || 0)}
              </p>
              <p className="text-xs text-gray-600">
                AOV: {formatCurrency(performance?.revenue?.average_order_value || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-md">
              <span className="text-purple-600 text-2xl">💸</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Comissões</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(performance?.revenue?.total_commissions || 0)}
              </p>
              <p className="text-xs text-green-600">Lucro líquido</p>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Performance */}
      {dashboardData?.platforms && (
        <div className="mb-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance por Plataforma</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(dashboardData.platforms).map(([platform, data]) => (
              <div key={platform} className="p-4 border rounded-lg">
                <h3 className="font-medium text-gray-900 capitalize mb-2">{platform}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cliques:</span>
                    <span className="font-medium">{formatNumber(data.clicks?.total || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Conversões:</span>
                    <span className="font-medium">{data.conversions?.total || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Taxa:</span>
                    <span className="font-medium text-green-600">
                      {(data.conversions?.rate || 0).toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Comissão:</span>
                    <span className="font-medium text-blue-600">
                      {formatCurrency(data.revenue?.total_commissions || 0)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Cliques Recentes</h2>
          <div className="space-y-3">
            {dashboardData?.recent_activity?.clicks?.slice(0, 5).map((click, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                <div>
                  <p className="font-medium text-sm">{click.platform?.toUpperCase()}</p>
                  <p className="text-xs text-gray-600">Produto: {click.product_id}</p>
                  <p className="text-xs text-gray-500">{click.country}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    {new Date(click.timestamp).toLocaleTimeString('pt-BR')}
                  </p>
                  <p className="text-xs text-gray-600">{click.device_type}</p>
                </div>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">Nenhum clique recente</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Conversões Recentes</h2>
          <div className="space-y-3">
            {dashboardData?.recent_activity?.conversions?.slice(0, 5).map((conversion, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-green-50 rounded">
                <div>
                  <p className="font-medium text-sm">{conversion.platform?.toUpperCase()}</p>
                  <p className="text-xs text-gray-600">
                    Venda: {formatCurrency(conversion.sale_amount, conversion.currency)}
                  </p>
                  <p className="text-xs text-green-600">
                    Comissão: {formatCurrency(conversion.commission_amount, conversion.currency)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    {new Date(conversion.timestamp).toLocaleTimeString('pt-BR')}
                  </p>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                    {conversion.status}
                  </span>
                </div>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">Nenhuma conversão recente</p>
            )}
          </div>
        </div>
      </div>

      {/* Auto-refresh indicator */}
      <div className="mt-8 text-center">
        <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full">
          <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse mr-2"></div>
          <span className="text-sm text-blue-600">
            Dashboard atualizando automaticamente a cada 30 segundos
          </span>
        </div>
      </div>
    </div>
  );
};

export default RealTimeDashboard;